import React, { useEffect, useState } from 'react';
import { FileText, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const imgSrc = (url) => (url && url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export const FlyerBuilder = () => {
  const { flyerConfig, flyerRegenerate, adminAlbums, adminAlbumPhotos, boardFlyerPdfUrl } = useAuth();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [album, setAlbum] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const max = cfg?.max_photos || 12;

  useEffect(() => {
    if (!open) return;
    flyerConfig().then((c) => { setCfg(c); setAlbum(c.album || ''); setSelected(c.photo_ids || []); }).catch(() => setCfg({}));
    adminAlbums().then((rows) => setAlbums(rows.filter((a) => a.count > 0))).catch(() => setAlbums([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!album) { setPhotos([]); return; }
    adminAlbumPhotos(album).then(setPhotos).catch(() => setPhotos([]));
  }, [album]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= max ? (toast.error(`Maximum ${max} photos`), s) : [...s, id]));
  const changeAlbum = (name) => { setAlbum(name); setSelected([]); };

  const run = async () => {
    setBusy(true);
    try {
      const r = await flyerRegenerate({ album, photo_ids: selected });
      toast.success(`Flyer regenerated — ${r.pages} pages`);
      setCfg((c) => ({ ...c, ...r }));
      window.open(`${boardFlyerPdfUrl()}?t=${Date.now()}`, '_blank', 'noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not regenerate flyer');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded font-title px-3 py-1.5 text-xs border" style={{ borderColor: 'var(--maroon)', color: 'var(--maroon)' }} data-testid="dashboard-flyer-regenerate"><RefreshCw size={14} /> Rebuild flyer (shop + album)</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="flyer-builder-dialog">
          <DialogHeader><DialogTitle className="font-title" style={{ color: 'var(--maroon)' }}>Rebuild board presentation flyer</DialogTitle></DialogHeader>
          <p className="text-xs" style={{ color: 'var(--olive)' }}>Adds a shop page built from the live product list and a photo page from the album you choose. Pick up to {max} photos; the first one is shown large.</p>
          {cfg?.generated_at && <div className="text-xs" style={{ color: 'var(--olive)' }} data-testid="flyer-last-generated">Last rebuilt {new Date(cfg.generated_at).toLocaleString()} by {cfg.generated_by}{cfg.album ? ` · album "${cfg.album}"` : ''}</div>}
          <label className="text-xs font-title block" style={{ color: 'var(--navy)' }}>Album for the photo page
            <select value={album} onChange={(e) => changeAlbum(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white" data-testid="flyer-album-select">
              <option value="">— No photo page —</option>
              {albums.map((a) => <option key={a.name} value={a.name}>{a.name} ({a.count})</option>)}
            </select>
          </label>
          {album && (
            <div>
              <div className="flex items-center justify-between text-xs mb-2" style={{ color: 'var(--olive)' }}>
                <span data-testid="flyer-selected-count">{selected.length} / {max} selected</span>
                <div className="flex gap-2">
                  <button className="underline" onClick={() => setSelected(photos.slice(0, max).map((p) => p.id))} data-testid="flyer-select-first">Select first {Math.min(max, photos.length)}</button>
                  <button className="underline" onClick={() => setSelected([])} data-testid="flyer-select-none">Clear</button>
                </div>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="flyer-photo-grid">
                {photos.map((p) => {
                  const idx = selected.indexOf(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => toggle(p.id)} title={p.title || p.id} className="relative aspect-square rounded overflow-hidden border-2 transition-transform hover:scale-[1.03]" style={{ borderColor: idx >= 0 ? 'var(--maroon)' : 'transparent' }} data-testid={`flyer-photo-${p.id}`} aria-pressed={idx >= 0}>
                      <img src={imgSrc(p.url)} alt={p.title || ''} className="w-full h-full object-cover" loading="lazy" />
                      {idx >= 0 && <span className="absolute top-1 left-1 w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{idx + 1}</span>}
                      {p.status && p.status !== 'approved' && <span className="absolute bottom-0 inset-x-0 text-[9px] bg-black/60 text-white text-center">{p.status}</span>}
                    </button>
                  );
                })}
                {photos.length === 0 && <div className="col-span-full text-xs py-4 text-center" style={{ color: 'var(--olive)' }}>No photos in this album.</div>}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <a href={boardFlyerPdfUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded font-title px-3 py-1.5 text-xs border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }} data-testid="flyer-open-current"><FileText size={14} /> Open current PDF</a>
            <button onClick={run} disabled={busy || (album && selected.length === 0)} className="inline-flex items-center gap-2 rounded font-title px-3 py-1.5 text-xs disabled:opacity-50" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="flyer-regenerate-submit">
              {busy ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />} {busy ? 'Building PDF…' : 'Rebuild flyer'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
